import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  message,
  Modal,
  Spin,
  Row,
  Col,
} from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { updateClient } from "../../api/client/clientRequest";
import { CLIENT_TYPES } from "../../utils/constants";
import { getPublicInstitutions } from "../../api/public_institutions/publicInstitutionRequest";
import { getMessageError } from "../../utils/messageUtils";
import PermissionRender from "../../components/PermissionRender";

const { Option } = Select;

const ModifyClientInfo = ({ client, onClientUpdated }) => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [clientType, setClientType] = useState(client?.type);

  useEffect(() => {
    if (isModalVisible && clientType === "institutions") {
      fetchInstitutions();
    }
  }, [clientType, isModalVisible]);

  useEffect(() => {
    setClientType(client?.type);
    if (client && isModalVisible) {
      setFormValues(client);
    }
  }, [client]);

  const fetchInstitutions = async () => {
    setLoading(true);
    try {
      const response = await getPublicInstitutions();
      setInstitutions(response);
    } catch (error) {
      console.error("Failed to fetch institutions:", error);
      message.error("Impossible de charger les Organismes publics");
    } finally {
      setLoading(false);
    }
  };

  const setFormValues = (currentClient) => {
    form.setFieldsValue({
      name: currentClient?.name,
      email: currentClient?.email,
      phone: currentClient?.phone,
      address: currentClient?.address,
      address2: currentClient?.address2,
      city: currentClient?.city,
      postal_code: currentClient?.postal_code,
      country: currentClient?.country,
      type: currentClient?.type,
      civility: currentClient?.civility,
      first_name: currentClient?.first_name,
      last_name: currentClient?.last_name,
      cin: currentClient?.cin,
      ice: currentClient?.ice,
      rc: currentClient?.rc,
      public_institution_id: currentClient?.public_institution?.id,
      contact_first_name: currentClient?.contact_first_name,
      contact_last_name: currentClient?.contact_last_name,
      contact_phone: currentClient?.contact_phone,
    });
  };

  const showModal = () => {
    setClientType(client?.type);
    setFormValues(client);
    setIsModalVisible(true);
    if (client?.type === "institutions") {
      fetchInstitutions();
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onTypeChange = (value) => {
    setClientType(value);
    form.setFieldsValue({
      cin: undefined,
      ice: undefined,
      rc: undefined,
      public_institution_id: undefined,
      contact_first_name: undefined,
      contact_last_name: undefined,
      contact_phone: undefined,
    });
    if (value === "institutions") {
      fetchInstitutions();
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    const payload = Object.fromEntries(
      Object.entries(values).filter(
        ([_, v]) => v !== null && v !== undefined && v !== ""
      )
    );

    if (payload.type !== "individuals") {
      delete payload.cin;
      delete payload.civility;
      delete payload.first_name;
      delete payload.last_name;
    }
    if (payload.type !== "companies") {
      delete payload.ice;
      delete payload.rc;
      delete payload.contact_first_name;
      delete payload.contact_last_name;
      delete payload.contact_phone;
    }
    if (payload.type !== "institutions") {
      delete payload.public_institution_id;
    }
    if (payload.type === "individuals") {
      delete payload.name;
    }

    try {
      const response = await updateClient(client.id, payload);
      message.success("Client mis à jour avec succès");
      setIsModalVisible(false);

      if (onClientUpdated) {
        onClientUpdated(response);
      }
    } catch (error) {
      console.error("Error updating client:", error?.response?.data?.error);
      getMessageError(
        error?.response?.data?.error,
        "Erreur lors de la mise à jour du client"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PermissionRender requiredPermission={["clients", "update"]}>
        <Button type="primary" icon={<EditOutlined />} onClick={showModal}>
          Modifier les informations
        </Button>
      </PermissionRender>

      <Modal
        title="Modifier les informations du client"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="type"
              label="Type de client"
              rules={[{ required: true, message: "Le type est requis" }]}
            >
              <Select
                onChange={onTypeChange}
                placeholder="Sélectionner un type"
              >
                {Object.entries(CLIENT_TYPES).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {clientType === "individuals" && (
              <>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="civility" label="Civilité">
                      <Select placeholder="Sélectionner">
                        <Option value="Mr">Mr</Option>
                        <Option value="Mme">Mme</Option>
                        <Option value="Mlle">Mlle</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="first_name"
                      label="Prénom"
                      rules={[
                        { required: true, message: "Le prénom est requis" },
                      ]}
                    >
                      <Input placeholder="Prénom" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="last_name"
                      label="Nom"
                      rules={[{ required: true, message: "Le nom est requis" }]}
                    >
                      <Input placeholder="Nom" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="cin"
                  label="CIN"
                  rules={[
                    { required: true, message: "Le CIN est obligatoire" },
                  ]}
                >
                  <Input placeholder="Numéro CIN" />
                </Form.Item>
              </>
            )}

            {clientType === "companies" && (
              <>
                <Form.Item
                  name="name"
                  label="Nom de la société"
                  rules={[
                    { required: true, message: "Le nom est obligatoire" },
                  ]}
                >
                  <Input placeholder="Nom de la société" />
                </Form.Item>
                <Form.Item
                  name="ice"
                  label="ICE"
                  rules={[{ required: true, message: "L'ICE est obligatoire" }]}
                >
                  <Input placeholder="Numéro ICE" />
                </Form.Item>
                <Form.Item name="rc" label="RC">
                  <Input placeholder="Numéro RC" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={24}>
                    <div className="text-md font-semibold mb-2">
                      Informations de Contact
                    </div>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="contact_first_name"
                      label="Prénom du Contact"
                    >
                      <Input placeholder="Prénom" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="contact_last_name" label="Nom du Contact">
                      <Input placeholder="Nom" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="contact_phone" label="Téléphone du Contact">
                  <Input placeholder="Téléphone" />
                </Form.Item>
              </>
            )}

            {clientType === "institutions" && (
              <>
                <Form.Item
                  name="name"
                  label="Nom de l'institution"
                  rules={[
                    { required: true, message: "Le nom est obligatoire" },
                  ]}
                >
                  <Input placeholder="Nom de l'institution" />
                </Form.Item>
                <Form.Item
                  name="public_institution_id"
                  label="Organisme public"
                  rules={[
                    {
                      required: true,
                      message: "L'Organisme public est obligatoire",
                    },
                  ]}
                >
                  <Select
                    placeholder="Sélectionner une Organisme public"
                    loading={loading && !institutions.length}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {institutions.map((institution) => (
                      <Option key={institution.id} value={institution.id}>
                        {institution.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: "email", message: "Format d'email invalide" }]}
            >
              <Input placeholder="Email du client" />
            </Form.Item>

            <Form.Item name="phone" label="Téléphone Principal">
              <Input placeholder="Numéro de téléphone principal" />
            </Form.Item>

            <Form.Item name="address" label="Adresse Ligne 1">
              <Input placeholder="Adresse Ligne 1" />
            </Form.Item>
            <Form.Item name="address2" label="Adresse Ligne 2">
              <Input placeholder="Adresse Ligne 2 (optionnel)" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="city" label="Ville">
                  <Input placeholder="Ville" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="postal_code" label="Code Postal">
                  <Input placeholder="Code Postal" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="country" label="Pays">
                  <Input placeholder="Pays" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item className="flex justify-end mt-6 mb-0">
              <Button
                onClick={handleCancel}
                icon={<CloseOutlined />}
                className="mr-2"
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Enregistrer
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default ModifyClientInfo;
